import {
  HappyFaceIcon,
  SadFaceIcon,
  SmileFaceIcon,
} from '@components/icons/FaceIcon';
import TooltipIconComponent from '@components/TooltipIconComponent/TooltipIconComponent';
import { useTheme } from 'context/ThemeProvider/ThemeProvider';
import { type FC, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';

export enum AttentionLevels {
  HIGH = 'high',
  MEDIUM = 'medium',
  LOW = 'low',
}
interface BuildingAttentionLevelProps {
  attention: AttentionLevels;
  isFetching?: boolean;
}
const BuildingAttentionLevel: FC<BuildingAttentionLevelProps> = ({
  attention,
  isFetching,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const attentionSpecificLayout = useMemo(() => {
    if (attention === AttentionLevels.HIGH) {
      return {
        fontColor: theme.colors.error,
        bgColor: theme.colors['status-bg'].error,
        text: t('dashboardPage.buildingAttentionLevel.attentionNeeded'),
        icon: <SadFaceIcon />,
        tooltip: t(
          'dashboardPage.buildingAttentionLevel.attentionNeededTooltip'
        ),
      };
    } else if (attention === AttentionLevels.MEDIUM) {
      return {
        fontColor: theme.colors.attention,
        bgColor: 'rgba(239, 131, 31, 0.10)',
        text: t('dashboardPage.buildingAttentionLevel.someActionNeeded'),
        icon: <SmileFaceIcon />,
        tooltip: undefined,
      };
    } else if (attention === AttentionLevels.LOW) {
      return {
        fontColor: theme.colors.status.solved,
        bgColor: '#23B44C1A',
        text: t('dashboardPage.buildingAttentionLevel.noActionRequired'),
        icon: <HappyFaceIcon />,
        tooltip: undefined,
      };
    }
  }, [attention]);
  return isFetching ? (
    <Skeleton height={48} width={180} />
  ) : (
    <div
      className="flex justify-between items-center gap-4 px-4 py-2.5 w-fit rounded-lg"
      style={{ backgroundColor: attentionSpecificLayout?.bgColor }}
    >
      {attentionSpecificLayout?.icon}
      <p
        className="text-lg"
        style={{ color: attentionSpecificLayout?.fontColor }}
      >
        {attentionSpecificLayout?.text}
      </p>
      {attentionSpecificLayout?.tooltip && (
        <TooltipIconComponent
          content={attentionSpecificLayout.tooltip}
          color={attentionSpecificLayout?.fontColor}
        />
      )}
    </div>
  );
};
export default BuildingAttentionLevel;
